import { renderPageShell } from '../lib/page-shell';
import { authorizedFetch, getToken } from '../lib/session';
import type { RouteHandler } from '../lib/router';

interface ChallengeView {
  id: string;
  period: 'daily' | 'weekly';
  name: string;
  description: string;
  xp: number;
  goal: number;
  progress: number;
  completed: boolean;
  rewarded: boolean;
  resetsAt: number;
}

/** A short "resets in 3h" / "resets in 2d" from now until `resetsAt`. */
function resetsIn(resetsAt: number, now: number): string {
  const ms = Math.max(0, resetsAt - now);
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours >= 48) return `Resets in ${Math.round(hours / 24)}d`;
  if (hours >= 1) return `Resets in ${hours}h`;
  const mins = Math.max(1, Math.floor(ms / (60 * 1000)));
  return `Resets in ${mins}m`;
}

function renderChallenge(c: ChallengeView, now: number): string {
  const pct = c.goal > 0 ? Math.round((Math.min(c.progress, c.goal) / c.goal) * 100) : 0;
  const state = c.rewarded ? 'challenge--done' : c.completed ? 'challenge--ready' : 'challenge--open';
  const status = c.rewarded ? 'Claimed ✓' : c.completed ? 'Complete' : `${c.progress} / ${c.goal}`;
  return `
    <li class="challenge ${state}">
      <div class="challenge__head">
        <div class="challenge__body">
          <strong>${c.name}</strong>
          <span>${c.description}</span>
        </div>
        <div class="challenge__meta">
          <em>+${c.xp} XP</em>
          <span class="challenge__status">${status}</span>
        </div>
      </div>
      <div class="challenge__bar" role="progressbar" aria-valuemin="0" aria-valuemax="${c.goal}" aria-valuenow="${Math.min(c.progress, c.goal)}">
        <span style="width:${pct}%"></span>
      </div>
      <p class="challenge__reset">${resetsIn(c.resetsAt, now)}</p>
    </li>`;
}

function renderGroup(title: string, challenges: ChallengeView[], now: number): string {
  if (challenges.length === 0) return '';
  const items = challenges.map((c) => renderChallenge(c, now)).join('');
  return `
    <h2 class="history-heading">${title}</h2>
    <ul class="challenge-list">${items}</ul>`;
}

function renderBoard(challenges: ChallengeView[], now: number): string {
  const daily = challenges.filter((c) => c.period === 'daily');
  const weekly = challenges.filter((c) => c.period === 'weekly');
  const claimed = challenges.filter((c) => c.rewarded).length;
  return `
    <p class="settings-hint">${claimed} of ${challenges.length} claimed. Rewards land in your XP the first time you complete each; progress resets when the period does.</p>
    ${renderGroup('Daily', daily, now)}
    ${renderGroup('Weekly', weekly, now)}`;
}

export const renderChallenges: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'CHALLENGES',
    subtitle: 'Daily and weekly goals. Each grants XP once, then refreshes with the period.',
    body: `<div id="challenges-mount"><p class="attempt-list__empty">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#challenges-mount')!;
  if (!getToken()) {
    mount.innerHTML = `<p class="settings-hint">Sign in on the Account page to take on challenges.</p>`;
    return;
  }

  authorizedFetch<{ challenges: ChallengeView[]; now: number }>('/api/challenges')
    .then(({ challenges, now }) => {
      mount.innerHTML = renderBoard(challenges, now ?? Date.now());
    })
    .catch(() => {
      mount.innerHTML = `<p class="attempt-list__empty">Could not load challenges.</p>`;
    });
};
